import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Rheostat from 'rheostat'
import classNames from 'classnames'
import { toFormErrors } from 'react-formal'
import { debounce, noop } from '../../../../lib/utils/common'
import { numberShortFormat, withCurrency } from '../../../../lib/utils/money'
import ErrorMessage from './ErrorMessage'
import Handle from './Handle'
import PriceCombobox from './PriceCombobox'
import priceRangeSchema from './schema'

const HISTOGRAM_CONFIG = {
  BAR_SPACING: 0.85,
  MAX_HEIGHT: 80,
  MIN_BAR_WIDTH: 0.4,
  MIN_HEIGHT: 5
}

const RENTAL_PRICE_OPTIONS = [
  1500, 2500, 5000, 10000, 15000, 20000,
  50000, 55000, 60000, 65000, 75000, 100000
]

const SALE_PRICE_OPTIONS = [
  150000, 250000, 350000, 400000, 450000,
  1000000, 1500000, 2000000, 2500000, 3000000, 3500000
]

class PriceInput extends Component {
  static propTypes = {
    className: PropTypes.string,
    handleTemplate: PropTypes.func,
    onChange: PropTypes.func,
    onReset: PropTypes.func,
    priceExtremum: PropTypes.shape({
      counts: PropTypes.object.isRequired,
      cutOff: PropTypes.number.isRequired,
      histogramMax: PropTypes.number.isRequired,
      histogramMin: PropTypes.number.isRequired,
      priceMax: PropTypes.number.isRequired,
      priceMin: PropTypes.number.isRequired
    }).isRequired,
    values: PropTypes.array.isRequired,
    view: PropTypes.string
  }

  static defaultProps = {
    handleTemplate: v => withCurrency(numberShortFormat(v)),
    onChange: noop,
    onReset: noop
  }

  constructor(props) {
    super(props)

    this.state = this.getInitialState(this.props)()
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const [newMin, newMax] = nextProps.values
    const [currentMin, currentMax] = this.props.values

    if (newMin !== currentMin || newMax !== currentMax) {
      this.setState(this.getInitialState(nextProps))
    }
  }

  getInitialState = ({ values }) => () => ({
    errors: {},
    priceFrom: values[0],
    priceTo: values[1],
    values
  })

  updateValues = props => {
    const normalizedValues = props.values
    const isRentals = this.priceOptions === RENTAL_PRICE_OPTIONS
    const maxValue = isRentals ? 500000 : 20000000
    const currentPriceTo = this.state.values[1]

    const actualValues = [
      normalizedValues[0],
      normalizedValues[1] === maxValue && currentPriceTo === null ? null : normalizedValues[1]
    ]

    this.setState(this.getInitialState({ values: actualValues }), this.notifyChange)
  }

  debouncedUpdateValues = debounce(this.updateValues, 200)

  notifyChange = () => {
    const { onChange, onReset, priceExtremum: { histogramMax, histogramMin } } = this.props
    const { values } = this.state

    onChange('priceFrom', values[0])
    onChange('priceTo', values[1])

    if (values[0] === histogramMin) onReset(['priceFrom'])
    if (values[1] === histogramMax) onReset(['priceTo'])
  }

  handlePriceChange = (name, value) => {
    this.setState({ [name]: value }, () => {
      const { priceFrom, priceTo } = this.state

      priceRangeSchema(this.allowedPriceMin, this.allowedPriceMax)
        .validate({ priceFrom, priceTo }, { abortEarly: false })
        .then(() => this.updateValues({ values: [priceFrom, priceTo] }))
        .catch(err => {
          this.setState(state => ({ ...state, errors: { ...toFormErrors(err) } }))
        })
    })
  }

  get allowedPriceMin() {
    const { priceExtremum: { histogramMin } } = this.props
    return histogramMin
  }

  get allowedPriceMax() {
    const { priceExtremum: { histogramMax } } = this.props
    return histogramMax
  }

  get priceOptions() {
    const { priceExtremum: { histogramMax } } = this.props
    return histogramMax === 500000 ? RENTAL_PRICE_OPTIONS : SALE_PRICE_OPTIONS
  }

  renderHistogramBars = () => {
    const { priceExtremum: { counts, histogramMax, histogramMin } } = this.props
    const [selectedMin, selectedMax] = this.sliderValues

    const pricePoints = Object.keys(counts)
      .map(price => parseInt(price, 10))
      .filter(price => price >= histogramMin && price <= histogramMax)
      .sort((a, b) => a - b)

    if (pricePoints.length === 0) return null

    const maxCount = Math.max(...pricePoints.map(price => counts[price]))
    const priceRange = histogramMax - histogramMin
    const barWidth = Math.max(
      (100 / pricePoints.length) * HISTOGRAM_CONFIG.BAR_SPACING,
      HISTOGRAM_CONFIG.MIN_BAR_WIDTH
    )

    return pricePoints.map(price => {
      const count = counts[price]
      const leftPercent = ((price - histogramMin) / priceRange) * 100
      const height = Math.max(
        (count / maxCount) * HISTOGRAM_CONFIG.MAX_HEIGHT,
        HISTOGRAM_CONFIG.MIN_HEIGHT
      )

      const isOutsideRange = price < selectedMin || price > selectedMax

      return (
        <div
          key={price}
          className={classNames('PriceInput-histogramBar', {
            'PriceInput-histogramBar--inactive': isOutsideRange
          })}
          style={{
            height: `${height}px`,
            left: `${leftPercent}%`,
            transform: 'translateX(-50%)',
            width: `${barWidth}%`
          }}
        />
      )
    })
  }

  get sliderValues() {
    const { values } = this.state
    const isRentals = this.priceOptions === RENTAL_PRICE_OPTIONS
    const maxValue = isRentals ? 500000 : 20000000

    return [
      values[0],
      values[1] !== null && values[1] !== undefined ? values[1] : maxValue
    ]
  }

  render() {
    const { className, handleTemplate, priceExtremum, view } = this.props
    const { errors, priceFrom, priceTo, values } = this.state
    const priceFromId = `priceFrom-${view || 'default'}`
    const priceToId = `priceTo-${view || 'default'}`

    return (
      <div className={classNames('PriceInput', className)}>
        <div className='PriceInput-header'>Price</div>
        <div className='PriceInput-slider'>
          <div className='PriceInput-histogram'>
            {this.renderHistogramBars()}
          </div>
          <Rheostat
            min={this.allowedPriceMin}
            max={this.allowedPriceMax}
            values={this.sliderValues}
            onValuesUpdated={this.debouncedUpdateValues}
            handle={Handle}
            handleTemplate={handleTemplate}
          />
        </div>
        <div className='PriceInput-fields'>
          <div className='PriceInput-field'>
            <PriceCombobox
              id={priceFromId}
              name='priceFrom'
              value={priceFrom}
              onChange={value => this.handlePriceChange('priceFrom', value)}
              options={this.priceOptions}
              placeholder='Min'
              isInvalid={Boolean(errors.priceFrom)}
            />
            {errors.priceFrom && <ErrorMessage ariaDescribedby={priceFromId} errors={errors.priceFrom} />}
          </div>

          <div className='PriceInput-field'>
            <PriceCombobox
              id={priceToId}
              name='priceTo'
              value={priceTo}
              onChange={value => this.handlePriceChange('priceTo', value)}
              options={this.priceOptions}
              placeholder='Max'
              isInvalid={Boolean(errors.priceTo)}
            />
            {errors.priceTo && <ErrorMessage ariaDescribedby={priceToId} errors={errors.priceTo} />}
          </div>
        </div>
      </div>
    )
  }
}

export default PriceInput
